"use client"

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import debounce from "lodash.debounce"
import { api } from "../../services/Axios"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import Loader from "../utils/loader/Loader"
import ReusableModal from "../utils/modals/ReusableModal"
import {
  FaMoneyBillWave,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaDownload,
  FaArrowDown,         // para representar egresos (salida de dinero)
  FaHandHoldingUsd,    // como si estuvieras entregando dinero
  FaCoins,
} from "react-icons/fa"
import PageHeader from "../ui/management/PageHeader"
import StatsCard from "../ui/management/StatsCard"
import Pagination from "../ui/management/Pagination"

export interface Expense {
  _id: string
  nombre: string
  tipo: string
  valorCOP: number
  valorCOPUnidad?: number
  proveedor?: { _id: string; nombre: string }
  fecha: Date
  cantidad?: number
  concepto?: string
  fechaPlazoMaximo?: Date
  numeroCuotas?: number
}

interface FilterOption {
  _id: string
  nombre: string
}

function ExpenseList() {
  // Estados para control general de la vista y modales
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false)
  const [gastos, setGastos] = useState<Expense[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState("")
  const [modalAction, setModalAction] = useState<(() => void) | null>(null)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [messageContent, setMessageContent] = useState("")
  const [messageType, setMessageType] = useState<"success" | "error" | "info" | "warning">("success")

  // Filtros de búsqueda: además de Tipo y Proveedor, se añaden filtros por fechas
  const [searchTerm, setSearchTerm] = useState("")
  const [filterTipo, setFilterTipo] = useState("")
  const [filterProveedor, setFilterProveedor] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  // Lista de proveedores para el filtro y para el modal
  const [suppliers, setSuppliers] = useState<FilterOption[]>([])

  // Estados para los campos del formulario de gasto y errores
  const [nombre, setNombre] = useState("")
  const [tipo, setTipo] = useState("")
  const [valorCOP, setValorCOP] = useState<number | "">("")
  const [valorCOPUnidad, setValorCOPUnidad] = useState<number | "">("")
  const [fecha, setFecha] = useState("")
  const [cantidad, setCantidad] = useState<number | "">("")
  const [concepto, setConcepto] = useState("")
  const [proveedor, setProveedor] = useState("")
  const [fechaPlazoMaximo, setFechaPlazoMaximo] = useState("")
  const [numeroCuotas, setNumeroCuotas] = useState<number | "">("")

  const [nombreError, setNombreError] = useState("")
  const [tipoError, setTipoError] = useState("")
  const [valorCOPError, setValorCOPError] = useState("")
  const [fechaError, setFechaError] = useState("")
  const [numeroCuotasError, setNumerocuotasError] = useState("")
  const [fechaMaximoError, setFechamaximoError] = useState("")

  // Estados para paginación
  const expensesPerPage = 6
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalExpenses, setTotalExpenses] = useState<number>(0)

  // Indicadores globales (dashboard)
  const [totalValorCOP, setTotalValorCOP] = useState<number>(0)
  const [totalCreditos, setTotalCreditos] = useState<number>(0)

  const searchInputRef = useRef<HTMLInputElement>(null)

  // Calcular el Valor Total (COP) del gasto
  useEffect(() => {
    const calcularValorGasto = () => {
      if (typeof valorCOPUnidad === "number" && valorCOPUnidad > 0) {
        let total = 0;
        if (tipo === "Crédito" && typeof numeroCuotas === "number" && numeroCuotas > 0) {
          total = valorCOPUnidad / numeroCuotas;
        } else {
          total = valorCOPUnidad * (typeof cantidad === "number" && cantidad > 0 ? cantidad : 1);
        }
        setValorCOP(Math.round(total));
      } else {
        setValorCOP("");
      }
    }
    calcularValorGasto();
  }, [valorCOPUnidad, numeroCuotas, cantidad, tipo]);

  // Obtener proveedores para el select del filtro y del modal
  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = () => {
    api.get("/api/proveedores")
      .then((response) => setSuppliers(response.data.proveedores || []))
      .catch((error) => console.error("Error al obtener proveedores:", error));
  };

  // Función para obtener gastos con filtros y paginación, incluyendo los filtros por fecha
  const fetchExpenses = useCallback(
    (page: number) => {
      setIsLoading(true);
      api.get("/api/gastos", {
        params: {
          page,
          limit: expensesPerPage,
          searchTerm: searchTerm || undefined,
          tipo: filterTipo || undefined,
          proveedor: filterProveedor || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      })
        .then((response) => {
          const { expenses, totalPages, totalExpenses, indicadores } = response.data;
          setGastos(expenses);
          setTotalPages(totalPages);
          setTotalExpenses(totalExpenses);
          setCurrentPage(page);

          // Actualizar indicadores globales
          setTotalValorCOP(indicadores.totalValorCOP);
          setTotalCreditos(indicadores.totalCreditos);
        })
        .catch((error) => console.error("Error al obtener gastos:", error))
        .finally(() => setIsLoading(false));
    },
    [expensesPerPage, searchTerm, filterTipo, filterProveedor, startDate, endDate]
  );

  useEffect(() => {
    fetchExpenses(currentPage);
  }, [fetchExpenses, currentPage]);

  // Debounce para la búsqueda
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
      }, 500),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterTipo("");
    setFilterProveedor("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    fetchExpenses(1);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  // Funciones para limpiar campos y errores del formulario
  const clearFields = () => {
    setNombre("");
    setTipo("");
    setValorCOPUnidad("");
    setValorCOP("");
    setFecha("");
    setCantidad("");
    setConcepto("");
    setProveedor("");
    setFechaPlazoMaximo("");
    setNumeroCuotas("");
    clearErrors();
  };

  const clearErrors = () => {
    setNombreError("");
    setTipoError("");
    setValorCOPError("");
    setFechaError("");
    setFechamaximoError("");
    setNumerocuotasError("");
  };

  // Funciones para el modal de confirmación
  const openConfirmationModal = (message: string, action?: () => void) => {
    setModalMessage(message);
    setModalAction(() => action || closeModal);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalAction(null);
    setModalMessage("");
  };

  // Acciones CRUD para gastos
  const handleAddExpense = () => {
    setIsEditMode(false);
    setIsModalOpen(true);
    clearFields();
  };

  const handleViewExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setNombre(expense.nombre);
    setTipo(expense.tipo);
    setValorCOP(expense.valorCOP);
    setValorCOPUnidad(expense.valorCOPUnidad || "");
    setFecha(expense.fecha ? new Date(expense.fecha).toISOString().split("T")[0] : "");
    setCantidad(expense.cantidad ?? "");
    setConcepto(expense.concepto || "");
    setProveedor(expense.proveedor ? expense.proveedor._id : "");
    setFechaPlazoMaximo(expense.fechaPlazoMaximo ? new Date(expense.fechaPlazoMaximo).toISOString().split("T")[0] : "");
    setNumeroCuotas(expense.numeroCuotas !== undefined ? expense.numeroCuotas : "");

    setIsEditMode(false);
    setIsReadOnlyMode(true);
    setIsModalOpen(true);
    clearErrors();
  };

  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setNombre(expense.nombre);
    setTipo(expense.tipo);
    setValorCOP(expense.valorCOP);
    setValorCOPUnidad(expense.valorCOPUnidad || "");
    setFecha(expense.fecha ? new Date(expense.fecha).toISOString().split("T")[0] : "");
    setCantidad(expense.cantidad ?? "");
    setConcepto(expense.concepto || "");
    setProveedor(expense.proveedor ? expense.proveedor._id : "");
    setFechaPlazoMaximo(expense.fechaPlazoMaximo ? new Date(expense.fechaPlazoMaximo).toISOString().split("T")[0] : "");
    setNumeroCuotas(expense.numeroCuotas !== undefined ? expense.numeroCuotas : "");

    setIsEditMode(true);
    setIsReadOnlyMode(false);
    setIsModalOpen(true);
    clearErrors();
  };

  const validateFields = () => {
    let hasError = false;
    if (!nombre.trim()) {
      setNombreError("Nombre es obligatorio");
      hasError = true;
    }
    if (!tipo.trim()) {
      setTipoError("Tipo es obligatorio");
      hasError = true;
    }
    if (valorCOPUnidad === "" || Number(valorCOPUnidad) <= 0) {
      setValorCOPError("El valor unidad debe ser mayor a cero");
      hasError = true;
    }
    if (!fecha.trim()) {
      setFechaError("Fecha es obligatoria");
      hasError = true;
    }
    if (tipo === "Crédito") {
      if (!fechaPlazoMaximo.trim()) {
        setFechamaximoError("La Fecha Plazo Máximo es obligatoria para crédito");
        hasError = true;
      } else if (new Date(fechaPlazoMaximo) < new Date(fecha)) {
        setFechamaximoError("La Fecha Plazo Máximo no puede ser menor que la Fecha");
        hasError = true;
      }
      if (!numeroCuotas || Number(numeroCuotas) <= 0) {
        setNumerocuotasError("Número de Cuotas es obligatorio y debe ser mayor a cero");
        hasError = true;
      }
    } else {
      setFechamaximoError("");
      setNumerocuotasError("");
    }
    return !hasError;
  };

  const handleSaveExpense = () => {
    if (!validateFields()) return;
    const expenseData: any = {
      nombre,
      tipo,
      valorCOPUnidad,
      valorCOP,
      fecha: fecha ? new Date(`${fecha}T12:00:00-05:00`) : undefined,
      cantidad: cantidad === "" ? undefined : cantidad,
      concepto: concepto || undefined,
      proveedor: proveedor === "" ? undefined : proveedor,
    };

    if (tipo === "Crédito") {
      expenseData.fechaPlazoMaximo = fechaPlazoMaximo ? new Date(fechaPlazoMaximo) : undefined;
      expenseData.numeroCuotas = numeroCuotas || undefined;
    }

    const saveAction = () => {
      if (isSaving) return; // 🔒 Previene múltiples ejecuciones
      setIsSaving(true)
      const action = isEditMode && selectedExpense
        ? api.put(`/api/gastos/${selectedExpense._id}`, expenseData)
        : api.post("/api/gastos/add", expenseData);

      action
        .then(() => {
          setMessageContent(isEditMode ? "Gasto actualizado exitosamente." : "Gasto agregado exitosamente.");
          setMessageType("success");
          setShowMessageModal(true);
          setIsModalOpen(false);
          fetchExpenses(currentPage);
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.msg || "Error al guardar el gasto.";
          setMessageContent(errorMsg);
          setMessageType("error");
          setShowMessageModal(true);
        })
        .finally(() => {
          setIsSaving(false)
          closeModal()
        })
    };

    openConfirmationModal(
      isEditMode
        ? "¿Deseas guardar los cambios realizados en este gasto?"
        : "¿Estás seguro de agregar este nuevo gasto?",
      saveAction
    );
  };

  const handleDeleteExpense = (expenseId: string) => {
    openConfirmationModal("¿Está seguro de que desea eliminar este gasto?", () => {
      api.delete(`/api/gastos/${expenseId}`)
        .then(() => {
          setMessageContent("Gasto eliminado exitosamente.");
          setMessageType("success");
          setShowMessageModal(true);
          setModalOpen(false);
          fetchExpenses(currentPage);
        })
        .catch(() => {
          setMessageContent("Error al eliminar el gasto.");
          setMessageType("error");
          setShowMessageModal(true);
          setModalOpen(false);
        })
        .finally(() => closeModal());
    });
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    closeModal();
  };

  // Función para formatear la fecha en formato local
  const formatLocalDate = (dateValue: Date | string) => {
    const date = new Date(dateValue);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    const day = date.getUTCDate().toString().padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  // Definición de campos para el modal reutilizable, replicando la UI de productos
  const fields = [
    { label: "Nombre del Gasto", value: nombre, onChange: setNombre, required: true, error: nombreError, type: "text" as const, readOnly: isReadOnlyMode },
    { label: "Concepto", value: concepto, onChange: setConcepto, type: "text" as const, readOnly: isReadOnlyMode },
    {
      label: "Tipo de Gasto",
      value: tipo,
      onChange: (value: string) => {
        setTipo(value);
        if (value === "Crédito") {
          setCantidad("");
        }
        setNombreError("");
        setTipoError("");
        setValorCOPError("");
        setFechaError("");
        setFechamaximoError("");
        setNumerocuotasError("");
      },
      required: true,
      type: "select" as const,
      options: [
        { value: "Factura de compra", label: "Factura de compra" },
        { value: "Pagos", label: "Pagos" },
        { value: "Recepción de comprobantes", label: "Recepción de comprobantes" },
        { value: "Documento soporte", label: "Documento soporte" },
        { value: "Crédito", label: "Crédito" }
      ],
      error: tipoError,
      readOnly: isReadOnlyMode,
    },
    {
      label: "Valor (COP) Unitario",
      value: valorCOPUnidad.toString(),
      onChange: (val: string) => setValorCOPUnidad(Number(val) || 0),
      required: true,
      error: valorCOPError,
      type: "text" as const,
      readOnly: isReadOnlyMode,
    },
    ...(tipo !== "Crédito"
      ? [
          {
            label: "Cantidad",
            value: cantidad?.toString() || "",
            onChange: (val: string) => setCantidad(Number(val) || ""),
            type: "text" as const,
            readOnly: isReadOnlyMode,
          },
        ]
      : [
          {
            label: "Número de Cuotas",
            value: numeroCuotas !== "" ? numeroCuotas.toString() : "",
            onChange: (val: string) => setNumeroCuotas(Number(val) || ""),
            required: true,
            error: numeroCuotasError,
            type: "text" as const,
            readOnly: isReadOnlyMode,
          },
        ]),
    {
      label: "Valor Total (COP)",
      value: valorCOP.toString(),
      onChange: () => {},
      readOnly: true,
      type: "text" as const,
    },
    {
      label: "Fecha",
      value: fecha,
      onChange: setFecha,
      type: "date" as const,
      required: true,
      error: fechaError,
      readOnly: isReadOnlyMode,
    },
    ...(tipo === "Crédito"
      ? [
          {
            label: "Fecha Plazo Máximo",
            value: fechaPlazoMaximo,
            onChange: setFechaPlazoMaximo,
            type: "date" as const,
            required: true,
            error: fechaMaximoError,
            readOnly: isReadOnlyMode,
          },
        ]
      : []),
    {
      label: "Proveedor",
      value: proveedor,
      onChange: setProveedor,
      type: "select" as const,
      options: suppliers.map((s) => ({ value: s._id, label: s.nombre })),
      readOnly: isReadOnlyMode,
    },
  ];

  const exportExpensesToCSV = async () => {
    if (startDate || endDate || filterTipo || filterProveedor || searchTerm) {
      alert("Debes quitar todos los filtros para exportar todos los gastos.");
      return;
    }
  
    try {
      const response = await api.get("/api/gastos", {
        params: {
          page: 1,
          limit: 10000, // Exporta hasta 10.000 gastos
        },
      });
  
      const exportData = response.data.expenses;
  
      if (!exportData || exportData.length === 0) {
        alert("No hay gastos para exportar.");
        return;
      }
  
      const headers = [
        "Fecha",
        "Nombre",
        "Tipo",
        "Valor Unitario",
        "Cantidad",
        "Valor Total",
        "Proveedor",
        "Concepto",
        "Fecha Límite",
        "Número de Cuotas"
      ];
  
      const rows = exportData.map((gasto: Expense) => [
        formatLocalDate(gasto.fecha),
        gasto.nombre || "",
        gasto.tipo || "",
        gasto.valorCOPUnidad ?? "",
        gasto.cantidad ?? "",
        gasto.valorCOP ?? "",
        gasto.proveedor?.nombre || "",
        gasto.concepto || "",
        gasto.fechaPlazoMaximo ? formatLocalDate(gasto.fechaPlazoMaximo) : "",
        gasto.numeroCuotas ?? ""
      ]);
  
      const csvContent = [
        headers.join(","),
        ...rows.map((row: (string | number)[]) =>
          row
            .map((value) =>
              typeof value === "string"
                ? `"${value.replace(/"/g, '""')}"`
                : value
            )
            .join(",")
        ),
      ].join("\n");
  
      const BOM = "\uFEFF"; // UTF-8 Byte Order Mark
      const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });

      const url = URL.createObjectURL(blob);
  
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `gastos-completos-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al exportar gastos:", error);
      alert("Error al exportar los gastos.");
    }
  };  

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {isLoading && <Loader />}
      <ConfirmationModal
        isOpen={modalOpen}
        title="Confirmación"
        message={modalMessage}
        onConfirm={modalAction || closeModal}
        onCancel={closeModal}
        isProcessing={isSaving}
      />
      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={handleCloseMessageModal}
      />

      {/* Page Header */}
      <PageHeader
        title="Gestión de Gastos"
        subtitle="Administra los gastos de tu organización"
        icon={FaMoneyBillWave}
        actionLabel="Agregar Gasto"
        onAction={handleAddExpense}
        actionIcon={FaPlus}
      />

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Gastos
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>
        {/* Filtros por fechas */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
              Fecha de Inicio
            </label>
            <div className="relative">
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200 text-gray-900 pl-3 pr-10 py-2"
              />
              <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
              Fecha de Fin
            </label>
            <div className="relative">
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200 text-gray-900 pl-3 pr-10 py-2"
              />
              <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre"
              onChange={handleSearchChange}
              className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
              ref={searchInputRef}
            />
          </div>

          {/* Tipo de Gasto Filter */}
          <div className="relative">
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="pl-3 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
            >
              <option value="">Todos los Tipos</option>
              <option value="Factura de compra">Factura de compra</option>
              <option value="Pagos">Pagos</option>
              <option value="Recepción de comprobantes">Recepción de comprobantes</option>
              <option value="Documento soporte">Documento soporte</option>
              <option value="Crédito">Crédito</option>
            </select>
          </div>

          {/* Proveedor Filter */}
          <div className="relative">
            <select
              value={filterProveedor}
              onChange={(e) => setFilterProveedor(e.target.value)}
              className="pl-3 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
            >
              <option value="">Todos los Proveedores</option>
              {suppliers.map((supplier) => (
                <option key={supplier._id} value={supplier._id}>
                  {supplier.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Cantidad Gastos"
          value={totalExpenses.toString()}
          description="Gastos filtrados"
          icon={FaArrowDown}
          gradient={false}
        />
        <StatsCard
          title="Total Créditos"
          value={totalCreditos.toString()}
          description="Créditos registrados"
          icon={FaHandHoldingUsd}
          gradient={false}
        />
        <StatsCard
          title="Total Gastos"
          value={`$${totalValorCOP ? totalValorCOP.toLocaleString() : 0}`}
          description="Monto total de gastos"
          icon={FaCoins}
          gradient={false}
        />
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={exportExpensesToCSV}
          disabled={!!startDate || !!endDate || !!filterTipo || !!filterProveedor || !!searchTerm}
          className={`py-2 px-4 font-semibold rounded-lg shadow flex items-center gap-2 transition 
            ${!!startDate || !!endDate || !!filterTipo || !!filterProveedor || !!searchTerm
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          title="Solo puedes exportar cuando no hay filtros activos"
        >
          <FaDownload className="inline-block mr-2" />
          Descargar Mis Gastos
        </button>
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {gastos.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor (COP)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {gastos.map((expense) => (
                  <tr key={expense._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">{formatLocalDate(expense.fecha)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{expense.nombre}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{expense.tipo}</td>
                    <td className="px-6 py-4 whitespace-nowrap">${expense.valorCOP.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleViewExpense(expense)}
                          title="Ver Gasto"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="bg-yellow-100 text-yellow-600 hover:bg-yellow-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleEditExpense(expense)}
                          title="Editar Gasto"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleDeleteExpense(expense._id)}
                          title="Eliminar Gasto"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-2 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaMoneyBillWave className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron gastos</h3>
            <p className="text-gray-500 mb-4">Intenta con otra búsqueda o agrega un nuevo gasto</p>
            <button
              onClick={handleAddExpense}
              className="flex items-center px-4 py-2 bg-[#2d53da] hover:bg-[#2d53da]/90 text-white rounded-lg transition-colors duration-200"
            >
              <FaPlus className="mr-2" /> Agregar Gasto
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Reusable Modal para Gasto */}
      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setIsReadOnlyMode(false)
        }}
        title={isReadOnlyMode ? "Ver Gasto" : isEditMode ? "Editar Gasto" : "Agregar Gasto"}
        fields={fields}
        onSave={handleSaveExpense}
        saveButtonText={isEditMode ? "Guardar Cambios" : "Agregar Gasto"}
        readOnlyMode={isReadOnlyMode}
      />
    </div>
  );
}

export default ExpenseList;
